import { useState, useEffect, useCallback, useRef } from "react";
import { authFetch } from "../lib/authFetch";
import { usePortailAuth } from "../lib/auth-context";
import { usePortailRouter } from "../lib/router-context";
import { useCart } from "../lib/cart-context";
import {
  ArrowLeft, Loader2, AlertCircle, Trash2, Plus, Minus,
  ShoppingCart, Send, CheckCircle2, Package, MapPin, Search,
  ChevronDown, ChevronUp, Shield, Wrench, X, RefreshCw, Calendar,
} from "lucide-react";

type TypeLigne = 'achat_robot' | 'achat_piece' | 'location' | 'extension' | 'forfait_deploiement' | 'maintenance';
type TypeDemande = 'achat' | 'location' | 'mixte';
type DureeLocation = 'jour' | 'semaine' | 'mois';

interface LigneDevis {
  id: string;
  typeLigne: TypeLigne;
  produitId?: string;
  extensionId?: string;
  nomFr: string;
  sku?: string;
  imageUrl?: string;
  puHt: number;
  quantite: number;
  remise: number;
  dureeLocation?: DureeLocation;
  nbPeriodes?: number;
  dateDebutLocation?: string;
  dateFinLocation?: string;
  financement24m?: number;
  financement36m?: number;
  financement48m?: number;
  financementChoisi?: '24' | '36' | '48' | null;
  // Snapshot features/specs pour les robots (capturé au moment de l'ajout)
  featuresSnapshot?: string[];
  specificationsSnapshot?: string[];
  featuresSelected?: boolean[];
  specificationsSelected?: boolean[];
}

interface CatalogueItem {
  id: string; sku: string | null; nomFr: string; type: string; imageUrl: string | null;
  prixAffiche: number | null; statutLabel: string;
  financement24m: number | null; financement36m: number | null; financement48m: number | null;
  locationDisponible: boolean | null;
  locationJourPartenaire: number | null; locationSemainePartenaire: number | null; locationMoisPartenaire: number | null;
  locationDelaiInstallation: string | null;
  forfaitDeploiement: number | null; maintenanceMensuelle: number | null;
  // Champs enrichis robots
  features?: string[] | null;
  specifications?: string[] | null;
  statutProduit?: string;
}

interface ExtensionDisponible {
  id: string; codeArticle: string; robotNom: string; dureeAns: number; prixRevendeur: string | null;
}

interface Adresse {
  id: string; label: string | null; adresse: string; codePostal: string | null; ville: string | null; contactNom: string | null;
}

interface DevisData {
  id: string; numero: string; statut: string; societe: string | null; contactNom: string | null;
  clientFinalNom: string | null; objet: string | null; notes: string | null; adresse: string | null;
  lignes: LigneDevis[]; remiseGlobale: string | null;
  typeDemande: string | null; environnement: string | null; dateLivraisonSouhaitee: string | null;
}

const ENVIRONNEMENTS = ["Grande distribution", "EHPAD", "Hôtellerie", "Industrie", "Restauration", "Autre"];
const DUREE_LABELS: Record<DureeLocation, string> = { jour: "Jour", semaine: "Semaine", mois: "Mois" };

function formatPrix(n: number | null | undefined) {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function calcLigneTotal(l: LigneDevis): number {
  const periods = l.typeLigne === 'location' ? (l.nbPeriodes || 1) : 1;
  return l.puHt * l.quantite * periods * (1 - l.remise / 100);
}

function InputQty({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      <button type="button" onClick={() => onChange(Math.max(1, value - 1))}
        className="w-6 h-6 flex items-center justify-center rounded border border-border text-foreground hover:bg-secondary/60">
        <Minus className="w-3 h-3" />
      </button>
      <input type="number" min={1} value={value}
        onChange={e => onChange(Math.max(1, parseInt(e.target.value) || 1))}
        className="w-12 text-center text-sm font-medium bg-background border border-border rounded py-0.5 focus:outline-none focus:ring-2 focus:ring-primary/30" />
      <button type="button" onClick={() => onChange(value + 1)}
        className="w-6 h-6 flex items-center justify-center rounded border border-border text-foreground hover:bg-secondary/60">
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
}

function ProductPickerModal({
  mode, primary, onClose, onSelect,
}: {
  mode: 'robot_achat' | 'piece' | 'robot_location';
  primary: string;
  onClose: () => void;
  onSelect: (item: CatalogueItem) => void;
}) {
  const [items, setItems] = useState<CatalogueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const type = mode === 'piece' ? 'pieces' : 'machine';
    authFetch(`/api/portail/catalogue?type=${type}`)
      .then(r => r.ok ? r.json() : { items: [] })
      .then(data => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [mode]);

  const filtered = items.filter(i =>
    (i.statutProduit !== 'a_venir') &&
    (i.nomFr.toLowerCase().includes(search.toLowerCase()) || (i.sku ?? "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">
            {mode === 'piece' ? "Choisir une pièce détachée" : mode === 'robot_location' ? "Choisir un robot en location" : "Choisir un robot / accessoire"}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary/60 text-muted-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-4 py-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              autoFocus
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou SKU…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-12">Aucun produit trouvé</p>
          ) : (
            <div className="divide-y divide-border/40">
              {filtered.map(item => {
                const hasLocation = item.locationJourPartenaire != null;
                return (
                  <button key={item.id} onClick={() => { onSelect(item); onClose(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/30 text-left transition-colors"
                    data-testid={`picker-item-${item.id}`}
                  >
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.nomFr} className="w-10 h-10 object-contain rounded border border-border flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded border border-border bg-muted flex items-center justify-center flex-shrink-0">
                        <Package className="w-4 h-4 text-muted-foreground/40" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{item.nomFr}</p>
                      <p className="text-xs text-muted-foreground font-mono">{item.sku}</p>
                    </div>
                    <div className="text-right shrink-0">
                      {mode === 'robot_location' ? (
                        <div>
                          {hasLocation ? (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Location dispo</span>
                          ) : (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Sur demande</span>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm font-semibold text-foreground">{formatPrix(item.prixAffiche)}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DevisBuilderPage({ id }: { id?: string }) {
  const { portailUser, brand } = usePortailAuth();
  const { navigate } = usePortailRouter();
  const { items: cartItems, clearCart } = useCart();
  const primary = brand?.couleurPrimaire || "#2563eb";

  const isNew = !id;
  const isEdit = !isNew;

  const [devisId, setDevisId] = useState<string | null>(id || null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [successSent, setSuccessSent] = useState(false);

  const [typeDemande, setTypeDemande] = useState<TypeDemande>('achat');
  const [adresses, setAdresses] = useState<Adresse[]>([]);
  const [adresseSelectId, setAdresseSelectId] = useState<string>("autre");
  const [adresseLibre, setAdresseLibre] = useState("");
  const [dateLivraisonSouhaitee, setDateLivraisonSouhaitee] = useState("");

  const [societe, setSociete] = useState(brand?.companyName || "");
  const [contactNom, setContactNom] = useState(
    portailUser ? `${portailUser.prenom || ""} ${portailUser.nom || ""}`.trim() : ""
  );
  const [clientFinalNom, setClientFinalNom] = useState("");
  const [objet, setObjet] = useState("");
  const [environnement, setEnvironnement] = useState("");
  const [notes, setNotes] = useState("");
  const [remiseGlobale, setRemiseGlobale] = useState(0);
  const [lignes, setLignes] = useState<LigneDevis[]>([]);

  // Extensions disponibles par produit (cache)
  const [extensionsCache, setExtensionsCache] = useState<Record<string, ExtensionDisponible[]>>({});
  const [expandedRobots, setExpandedRobots] = useState<Record<string, boolean>>({});

  // Product picker
  const [pickerMode, setPickerMode] = useState<'robot_achat' | 'piece' | 'robot_location' | null>(null);

  useEffect(() => {
    authFetch("/api/portail/adresses")
      .then(r => r.ok ? r.json() : [])
      .then(data => setAdresses(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      setLoading(true);
      authFetch(`/api/portail/devis/${id}`)
        .then(r => r.ok ? r.json() : Promise.reject(new Error("Devis introuvable")))
        .then((d: DevisData) => {
          setSociete(d.societe || "");
          setContactNom(d.contactNom || "");
          setClientFinalNom(d.clientFinalNom || "");
          setObjet(d.objet || "");
          setNotes(d.notes || "");
          setRemiseGlobale(Number(d.remiseGlobale) || 0);
          setTypeDemande((d.typeDemande as TypeDemande) || 'achat');
          setEnvironnement(d.environnement || "");
          setDateLivraisonSouhaitee(d.dateLivraisonSouhaitee || "");
          setLignes((d.lignes || []).map(l => ({
            id: l.id || crypto.randomUUID(),
            typeLigne: l.typeLigne || 'achat_robot',
            produitId: l.produitId,
            extensionId: l.extensionId,
            nomFr: l.nomFr,
            sku: l.sku,
            imageUrl: l.imageUrl,
            puHt: Number(l.puHt) || 0,
            quantite: Number(l.quantite) || 1,
            remise: Number(l.remise) || 0,
            dureeLocation: l.dureeLocation,
            nbPeriodes: l.nbPeriodes || 1,
            dateDebutLocation: l.dateDebutLocation,
            dateFinLocation: l.dateFinLocation,
            financement24m: l.financement24m,
            financement36m: l.financement36m,
            financement48m: l.financement48m,
            financementChoisi: l.financementChoisi,
          })));
          if (d.adresse) setAdresseLibre(d.adresse);
        })
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    } else if (isNew && cartItems.length > 0) {
      const initialLignes: LigneDevis[] = cartItems.map(item => ({
        id: crypto.randomUUID(),
        typeLigne: 'achat_robot' as TypeLigne,
        produitId: item.productId,
        nomFr: item.nomFr,
        sku: item.sku,
        imageUrl: item.imageUrl || undefined,
        puHt: item.prixAffiche || 0,
        quantite: item.qty,
        remise: 0,
      }));
      setLignes(initialLignes);
    }
  }, [id, isEdit, isNew]);

  // Charger extensions pour les robots achat
  useEffect(() => {
    const robotIds = lignes.filter(l => l.typeLigne === 'achat_robot' && l.produitId).map(l => l.produitId!);
    for (const pid of robotIds) {
      if (!extensionsCache[pid]) {
        authFetch(`/api/portail/extensions/${pid}`)
          .then(r => r.ok ? r.json() : [])
          .then(data => setExtensionsCache(prev => ({ ...prev, [pid]: Array.isArray(data) ? data : [] })))
          .catch(() => {});
      }
    }
  }, [lignes]);

  const adresseFormatted = useCallback(() => {
    if (adresseSelectId === "autre") return adresseLibre;
    const found = adresses.find(a => a.id === adresseSelectId);
    if (!found) return adresseLibre;
    return [found.adresse, found.codePostal && found.ville ? `${found.codePostal} ${found.ville}` : null, found.contactNom].filter(Boolean).join("\n");
  }, [adresseSelectId, adresseLibre, adresses]);

  const buildPayload = () => ({
    clientFinalNom, objet, notes, remiseGlobale,
    adresseLivraison: adresseFormatted(),
    typeDemande, environnement: environnement || null, dateLivraisonSouhaitee: dateLivraisonSouhaitee || null,
    lignes: lignes.map(l => ({
      id: l.id, typeLigne: l.typeLigne, produitId: l.produitId, extensionId: l.extensionId,
      nomFr: l.nomFr, sku: l.sku, imageUrl: l.imageUrl, puHt: l.puHt, quantite: l.quantite, remise: l.remise,
      dureeLocation: l.dureeLocation, nbPeriodes: l.nbPeriodes,
      dateDebutLocation: l.dateDebutLocation, dateFinLocation: l.dateFinLocation,
      financement24m: l.financement24m, financement36m: l.financement36m, financement48m: l.financement48m,
      financementChoisi: l.financementChoisi,
      featuresSnapshot: l.featuresSnapshot, specificationsSnapshot: l.specificationsSnapshot,
      featuresSelected: l.featuresSelected, specificationsSelected: l.specificationsSelected,
    })),
  });

  async function saveDevis(): Promise<string | null> {
    setSaving(true); setError(null);
    try {
      const payload = buildPayload();
      if (!devisId) {
        const res = await authFetch("/api/portail/devis", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) { const err = await res.json(); setError(err.message || "Erreur"); return null; }
        const data = await res.json();
        setDevisId(data.id);
        setLignes((data.lignes || []).map((l: any) => ({ ...l, puHt: Number(l.puHt) || 0, quantite: Number(l.quantite) || 1, remise: Number(l.remise) || 0 })));
        return data.id;
      } else {
        const res = await authFetch(`/api/portail/devis/${devisId}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) { const err = await res.json(); setError(err.message || "Erreur"); return null; }
        const data = await res.json();
        setLignes((data.lignes || []).map((l: any) => ({ ...l, puHt: Number(l.puHt) || 0, quantite: Number(l.quantite) || 1, remise: Number(l.remise) || 0 })));
        return devisId;
      }
    } finally { setSaving(false); }
  }

  async function handleEnvoyer() {
    setShowConfirmModal(false); setSending(true); setError(null);
    try {
      let did = devisId;
      if (!did) { did = await saveDevis(); if (!did) { setSending(false); return; } }
      // Save latest state before sending
      await authFetch(`/api/portail/devis/${did}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      const res = await authFetch(`/api/portail/devis/${did}/envoyer`, { method: "POST" });
      if (!res.ok) { const err = await res.json(); setError(err.message || "Erreur"); setSending(false); return; }
      clearCart();
      setSuccessSent(true);
    } catch { setError("Erreur réseau. Veuillez réessayer."); } finally { setSending(false); }
  }

  function addLigne(item: CatalogueItem, tl: TypeLigne, opts: Partial<LigneDevis> = {}) {
    const base: LigneDevis = {
      id: crypto.randomUUID(), typeLigne: tl,
      produitId: item.id, nomFr: item.nomFr, sku: item.sku ?? undefined, imageUrl: item.imageUrl || undefined,
      puHt: item.prixAffiche || 0, quantite: 1, remise: 0,
      financement24m: item.financement24m || undefined,
      financement36m: item.financement36m || undefined,
      financement48m: item.financement48m || undefined,
      ...opts,
    };
    if (tl === 'location') {
      base.dureeLocation = 'semaine';
      base.nbPeriodes = 1;
      base.puHt = item.locationSemainePartenaire || item.locationJourPartenaire || 0;
    }
    // Snapshot features/specs pour les robots au moment de l'ajout
    if (item.type === 'robot') {
      const feats = item.features ?? [];
      const specs = item.specifications ?? [];
      base.featuresSnapshot = feats;
      base.specificationsSnapshot = specs;
      base.featuresSelected = feats.map(() => true);
      base.specificationsSelected = specs.map(() => true);
    }
    setLignes(prev => [...prev, base]);
  }

  function updateLigne(ligneId: string, updates: Partial<LigneDevis>) {
    setLignes(prev => prev.map(l => l.id === ligneId ? { ...l, ...updates } : l));
  }

  function removeLigne(ligneId: string) {
    setLignes(prev => prev.filter(l => l.id !== ligneId));
  }

  function toggleExtension(robot: LigneDevis, ext: ExtensionDisponible) {
    const existing = lignes.find(l => l.typeLigne === 'extension' && l.extensionId === ext.id && l.produitId === robot.produitId);
    if (existing) {
      removeLigne(existing.id);
    } else {
      const newLigne: LigneDevis = {
        id: crypto.randomUUID(), typeLigne: 'extension',
        produitId: robot.produitId, extensionId: ext.id,
        nomFr: `Extension garantie ${ext.dureeAns} an${ext.dureeAns > 1 ? 's' : ''} — ${robot.nomFr}`,
        sku: ext.codeArticle, puHt: ext.prixRevendeur ? parseFloat(ext.prixRevendeur) : 0,
        quantite: robot.quantite, remise: 0,
      };
      setLignes(prev => {
        const idx = prev.findIndex(l => l.id === robot.id);
        const next = [...prev];
        next.splice(idx + 1, 0, newLigne);
        return next;
      });
    }
  }

  function addService(robot: LigneDevis, tl: 'forfait_deploiement' | 'maintenance') {
    const existing = lignes.find(l => l.typeLigne === tl && l.produitId === robot.produitId);
    if (existing) { removeLigne(existing.id); return; }
    const nomFr = tl === 'forfait_deploiement' ? `Forfait déploiement — ${robot.nomFr}` : `Maintenance mensuelle — ${robot.nomFr}`;
    setLignes(prev => {
      const idx = prev.findIndex(l => l.id === robot.id);
      const next = [...prev];
      next.splice(idx + 1, 0, {
        id: crypto.randomUUID(), typeLigne: tl,
        produitId: robot.produitId, nomFr, sku: robot.sku,
        puHt: 0, quantite: 1, remise: 0,
      });
      return next;
    });
  }

  // Calcul totaux
  const lignesAchat = lignes.filter(l => ['achat_robot', 'achat_piece', 'extension', 'forfait_deploiement', 'maintenance'].includes(l.typeLigne));
  const lignesLocation = lignes.filter(l => l.typeLigne === 'location');
  const sousTotalAchat = lignesAchat.reduce((s, l) => s + calcLigneTotal(l), 0);
  const sousTotalLocation = lignesLocation.reduce((s, l) => s + calcLigneTotal(l), 0);
  const sousTotal = sousTotalAchat + sousTotalLocation;
  const totalHT = sousTotal * (1 - remiseGlobale / 100);
  const tva = totalHT * 0.2;
  const ttc = totalHT + tva;

  const showAchat = typeDemande === 'achat' || typeDemande === 'mixte';
  const showLocation = typeDemande === 'location' || typeDemande === 'mixte';
  const hasFinancement = lignes.some(l => l.typeLigne === 'achat_robot' && (l.financement24m || l.financement36m || l.financement48m));

  if (loading) return (
    <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
  );

  if (successSent) return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center space-y-5 max-w-md">
        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-9 h-9 text-green-600 dark:text-green-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Demande envoyée !</h1>
          <p className="text-muted-foreground mt-2">Votre demande a bien été transmise à l'équipe Jobotto. Vous recevrez une réponse dans les meilleurs délais.</p>
        </div>
        <button onClick={() => navigate("/devis")} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90" style={{ backgroundColor: primary }}>
          Voir mes devis
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 border-b border-border/60 bg-card sticky top-0 z-10">
        <button onClick={() => navigate("/devis")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="button-back-devis">
          <ArrowLeft className="w-4 h-4" /> Retour aux devis
        </button>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-foreground">{isNew ? "Nouveau devis" : "Modifier le devis"}</h1>
            {/* Type selector */}
            <div className="flex items-center gap-1 bg-muted/60 rounded-lg p-0.5 text-xs font-medium">
              {(['achat', 'location', 'mixte'] as TypeDemande[]).map(t => (
                <button key={t} onClick={() => setTypeDemande(t)} data-testid={`btn-type-${t}`}
                  className={`px-2.5 py-1.5 rounded-md transition-colors ${typeDemande === t ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                  {t === 'achat' ? 'Achat' : t === 'location' ? 'Location' : 'Mixte'}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={async () => { await saveDevis(); navigate("/devis"); }} disabled={saving || sending}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-border text-foreground hover:bg-secondary/60 transition-colors disabled:opacity-60"
              data-testid="button-save-draft">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enregistrer le brouillon
            </button>
            <button onClick={() => setShowConfirmModal(true)} disabled={saving || sending || lignes.filter(l => ['achat_robot','achat_piece','location'].includes(l.typeLigne)).length === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: primary }} data-testid="button-envoyer-devis">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Envoyer ma demande
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {error && (
          <div className="flex items-center gap-2 text-destructive bg-destructive/10 rounded-lg p-3 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </div>
        )}

        {/* Destinataire */}
        <div className="bg-card border border-border/50 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">Destinataire & contexte</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Société</label>
              <input value={societe} readOnly className="w-full px-3 py-2 rounded-lg border border-border bg-muted text-sm text-muted-foreground cursor-not-allowed" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Contact</label>
              <input value={contactNom} readOnly className="w-full px-3 py-2 rounded-lg border border-border bg-muted text-sm text-muted-foreground cursor-not-allowed" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Client final</label>
              <input value={clientFinalNom} onChange={e => setClientFinalNom(e.target.value)}
                placeholder="Le magasin ou établissement concerné"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="input-client-final" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Objet de la demande</label>
              <input value={objet} onChange={e => setObjet(e.target.value)}
                placeholder="Ex : Achat robot + pièces de rechange"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="input-objet" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Type d'environnement <span className="text-muted-foreground/60">(optionnel)</span></label>
              <select value={environnement} onChange={e => setEnvironnement(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="select-environnement">
                <option value="">— Sélectionner —</option>
                {ENVIRONNEMENTS.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Lignes achat */}
        {showAchat && (
          <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border/60 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">
                Produits à l'achat ({lignesAchat.filter(l => ['achat_robot','achat_piece'].includes(l.typeLigne)).length})
              </h2>
              <div className="flex items-center gap-2">
                <button onClick={() => setPickerMode('robot_achat')}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-border text-foreground hover:bg-secondary/60 transition-colors"
                  data-testid="button-ajouter-robot-achat">
                  <Plus className="w-3.5 h-3.5" /> Robot / accessoire
                </button>
              </div>
            </div>
            {lignesAchat.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
                <Package className="w-10 h-10 opacity-20" />
                <p className="text-sm">Aucun produit — cliquez sur "+ Robot / accessoire"</p>
              </div>
            ) : (
              <div className="divide-y divide-border/30">
                {lignesAchat.map(l => {
                  const total = calcLigneTotal(l);
                  const isRobot = l.typeLigne === 'achat_robot';
                  const isSubLine = ['extension','forfait_deploiement','maintenance'].includes(l.typeLigne);
                  const extensions = l.produitId ? (extensionsCache[l.produitId] || []) : [];
                  const isExpanded = expandedRobots[l.id];
                  const hasExtEnabled = (eid: string) => lignes.some(x => x.typeLigne === 'extension' && x.extensionId === eid && x.produitId === l.produitId);
                  const hasDeploi = lignes.some(x => x.typeLigne === 'forfait_deploiement' && x.produitId === l.produitId);
                  const hasMaint = lignes.some(x => x.typeLigne === 'maintenance' && x.produitId === l.produitId);
                  return (
                    <div key={l.id} className={isSubLine ? "bg-muted/20" : ""}>
                      <div className="flex items-center gap-3 px-4 py-3" data-testid={`row-ligne-${l.id}`}>
                        {isSubLine ? (
                          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                            {l.typeLigne === 'extension' ? <Shield className="w-4 h-4 text-blue-500" /> : <Wrench className="w-4 h-4 text-muted-foreground" />}
                          </div>
                        ) : l.imageUrl ? (
                          <img src={l.imageUrl} alt={l.nomFr} className="w-10 h-10 object-contain rounded border border-border flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded border border-border bg-muted flex items-center justify-center flex-shrink-0">
                            <Package className="w-4 h-4 text-muted-foreground/40" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-foreground ${isSubLine ? 'text-xs' : 'text-sm'}`}>{l.nomFr}</p>
                          {l.sku && <p className="text-xs text-muted-foreground font-mono">{l.sku}</p>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-sm text-muted-foreground tabular-nums hidden sm:block">{formatPrix(l.puHt)}</span>
                          <div className="flex justify-end">
                            <InputQty value={l.quantite} onChange={v => updateLigne(l.id, { quantite: v })} />
                          </div>
                          <div className="flex items-center gap-1">
                            <input type="number" min={0} max={100} value={l.remise}
                              onChange={e => updateLigne(l.id, { remise: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) })}
                              className="w-12 text-right text-xs bg-background border border-border rounded px-1.5 py-1 focus:outline-none"
                              data-testid={`input-remise-${l.id}`} />
                            <span className="text-muted-foreground text-xs">%</span>
                          </div>
                          <span className="text-sm font-semibold tabular-nums w-20 text-right">{formatPrix(total)}</span>
                          <button onClick={() => removeLigne(l.id)} className="text-muted-foreground hover:text-destructive" data-testid={`button-remove-${l.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {isRobot && (
                          <button onClick={() => setExpandedRobots(p => ({ ...p, [l.id]: !p[l.id] }))}
                            className="text-muted-foreground hover:text-foreground ml-1" title="Options">
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                      {/* Options robot */}
                      {isRobot && isExpanded && (
                        <div className="px-4 pb-3 ml-13 bg-muted/20 border-t border-border/30 pt-3 space-y-3">
                          {extensions.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1.5">Extensions de garantie</p>
                              <div className="flex flex-wrap gap-2">
                                {extensions.map(ext => (
                                  <button key={ext.id} onClick={() => toggleExtension(l, ext)}
                                    className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${hasExtEnabled(ext.id) ? 'border-blue-400 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'border-border bg-background text-foreground hover:bg-secondary/60'}`}>
                                    <Shield className="w-3 h-3" />
                                    {ext.dureeAns} an{ext.dureeAns > 1 ? 's' : ''} — {ext.prixRevendeur ? `${parseFloat(ext.prixRevendeur).toFixed(2)} €` : 'Sur devis'}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => addService(l, 'forfait_deploiement')}
                              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${hasDeploi ? 'border-violet-400 bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' : 'border-border bg-background text-foreground hover:bg-secondary/60'}`}>
                              <Wrench className="w-3 h-3" /> {hasDeploi ? '✓ Forfait déploiement' : '+ Forfait déploiement'}
                            </button>
                            <button onClick={() => addService(l, 'maintenance')}
                              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${hasMaint ? 'border-violet-400 bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300' : 'border-border bg-background text-foreground hover:bg-secondary/60'}`}>
                              <RefreshCw className="w-3 h-3" /> {hasMaint ? '✓ Maintenance mensuelle' : '+ Maintenance mensuelle'}
                            </button>
                          </div>
                          {/* Financement */}
                          {(l.financement24m || l.financement36m || l.financement48m) && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1.5">Financement souhaité</p>
                              <div className="flex flex-wrap gap-2">
                                {([['24', l.financement24m], ['36', l.financement36m], ['48', l.financement48m]] as [string, number | undefined][]).filter(([,v]) => v).map(([mois, val]) => (
                                  <button key={mois} onClick={() => updateLigne(l.id, { financementChoisi: l.financementChoisi === mois as any ? null : mois as any })}
                                    className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${l.financementChoisi === mois ? 'border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'border-border bg-background text-foreground hover:bg-secondary/60'}`}>
                                    {mois} mois — {formatPrix(val)}/m
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Snapshot Caractéristiques & Spécifications */}
                          {(l.featuresSnapshot && l.featuresSnapshot.length > 0) && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1.5">Caractéristiques incluses dans le devis</p>
                              <div className="grid grid-cols-1 gap-1">
                                {l.featuresSnapshot.map((feat, idx) => (
                                  <label key={idx} className="flex items-start gap-2 cursor-pointer group">
                                    <input
                                      type="checkbox"
                                      checked={l.featuresSelected?.[idx] ?? true}
                                      onChange={() => {
                                        const next = [...(l.featuresSelected ?? l.featuresSnapshot!.map(() => true))];
                                        next[idx] = !next[idx];
                                        updateLigne(l.id, { featuresSelected: next });
                                      }}
                                      className="mt-0.5 w-3.5 h-3.5 rounded border-border accent-primary shrink-0"
                                    />
                                    <span className={`text-xs leading-relaxed ${(l.featuresSelected?.[idx] ?? true) ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                                      {feat}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                          {(l.specificationsSnapshot && l.specificationsSnapshot.length > 0) && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1.5">Spécifications incluses dans le devis</p>
                              <div className="grid grid-cols-1 gap-1">
                                {l.specificationsSnapshot.map((spec, idx) => (
                                  <label key={idx} className="flex items-start gap-2 cursor-pointer group">
                                    <input
                                      type="checkbox"
                                      checked={l.specificationsSelected?.[idx] ?? true}
                                      onChange={() => {
                                        const next = [...(l.specificationsSelected ?? l.specificationsSnapshot!.map(() => true))];
                                        next[idx] = !next[idx];
                                        updateLigne(l.id, { specificationsSelected: next });
                                      }}
                                      className="mt-0.5 w-3.5 h-3.5 rounded border-border accent-primary shrink-0"
                                    />
                                    <span className={`text-xs leading-relaxed ${(l.specificationsSelected?.[idx] ?? true) ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                                      {spec}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Lignes location */}
        {showLocation && (
          <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border/60 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">Robots en location ({lignesLocation.length})</h2>
              <button onClick={() => setPickerMode('robot_location')}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-border text-foreground hover:bg-secondary/60 transition-colors"
                data-testid="button-ajouter-robot-location">
                <Plus className="w-3.5 h-3.5" /> Ajouter un robot
              </button>
            </div>
            {lignesLocation.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
                <RefreshCw className="w-10 h-10 opacity-20" />
                <p className="text-sm">Aucun robot en location</p>
              </div>
            ) : (
              <div className="divide-y divide-border/30">
                {lignesLocation.map(l => {
                  const pricePerPeriod = l.puHt;
                  const total = calcLigneTotal(l);
                  return (
                    <div key={l.id} className="px-4 py-4 space-y-3" data-testid={`row-location-${l.id}`}>
                      <div className="flex items-start gap-3">
                        {l.imageUrl ? (
                          <img src={l.imageUrl} alt={l.nomFr} className="w-10 h-10 object-contain rounded border border-border flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded border border-border bg-muted flex items-center justify-center flex-shrink-0">
                            <RefreshCw className="w-4 h-4 text-muted-foreground/40" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">{l.nomFr}</p>
                          {l.sku && <p className="text-xs text-muted-foreground font-mono">{l.sku}</p>}
                        </div>
                        <span className="text-sm font-semibold tabular-nums">{formatPrix(total)}</span>
                        <button onClick={() => removeLigne(l.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Durée</label>
                          <div className="flex gap-1">
                            {(['jour', 'semaine', 'mois'] as DureeLocation[]).map(d => (
                              <button key={d} onClick={() => updateLigne(l.id, { dureeLocation: d })}
                                className={`flex-1 text-xs py-1 rounded border transition-colors ${l.dureeLocation === d ? 'bg-primary text-white border-primary' : 'border-border text-foreground hover:bg-secondary/60'}`}>
                                {DUREE_LABELS[d]}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Robots</label>
                          <InputQty value={l.quantite} onChange={v => updateLigne(l.id, { quantite: v })} />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Nombre de périodes</label>
                          <InputQty value={l.nbPeriodes || 1} onChange={v => updateLigne(l.id, { nbPeriodes: v })} />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground">Prix / période</label>
                          <p className="text-sm font-medium pt-1">{formatPrix(pricePerPeriod)}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="w-3 h-3" /> Date de début</label>
                          <input type="date" value={l.dateDebutLocation || ''} onChange={e => updateLigne(l.id, { dateDebutLocation: e.target.value })}
                            className="w-full px-2 py-1.5 text-xs rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="w-3 h-3" /> Date de fin</label>
                          <input type="date" value={l.dateFinLocation || ''} onChange={e => updateLigne(l.id, { dateFinLocation: e.target.value })}
                            className="w-full px-2 py-1.5 text-xs rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="px-5 py-3 bg-muted/10 border-t border-border/30">
              <p className="text-xs text-muted-foreground">Les tarifs de location incluent le robot uniquement. Livraison, installation et programmation sont facturées séparément selon le modèle.</p>
            </div>
          </div>
        )}

        {/* Adresse + Date livraison + Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card border border-border/50 rounded-xl p-5 space-y-4">
            <h2 className="text-sm font-semibold text-foreground">Livraison & notes</h2>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Adresse de livraison</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <select value={adresseSelectId} onChange={e => setAdresseSelectId(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="select-adresse-livraison">
                  {adresses.map(a => (
                    <option key={a.id} value={a.id}>{a.label || a.adresse} {a.ville ? `— ${a.ville}` : ""}</option>
                  ))}
                  <option value="autre">Autre adresse…</option>
                </select>
              </div>
            </div>
            {adresseSelectId === "autre" && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Adresse complète</label>
                <textarea rows={3} value={adresseLibre} onChange={e => setAdresseLibre(e.target.value)}
                  placeholder="Adresse de livraison…"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  data-testid="textarea-adresse-libre" />
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> Date de livraison souhaitée
              </label>
              <input type="date" value={dateLivraisonSouhaitee} onChange={e => setDateLivraisonSouhaitee(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="input-date-livraison" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Notes / précisions</label>
              <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Informations complémentaires, délais souhaités…"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                data-testid="textarea-notes" />
            </div>
          </div>

          {/* Récapitulatif */}
          <div className="bg-card border border-border/50 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-foreground mb-4">Récapitulatif</h2>
            <div className="space-y-2.5 text-sm">
              {typeDemande === 'mixte' ? (
                <>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Sous-total Achat HT</span><span className="tabular-nums">{formatPrix(sousTotalAchat)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground">
                    <span>Sous-total Location HT</span><span className="tabular-nums">{formatPrix(sousTotalLocation)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-muted-foreground">
                  <span>Sous-total HT</span><span className="tabular-nums">{formatPrix(sousTotal)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Remise globale</span>
                <div className="flex items-center gap-1">
                  <input type="number" min={0} max={100} value={remiseGlobale}
                    onChange={e => setRemiseGlobale(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                    className="w-14 text-right text-sm bg-background border border-border rounded px-2 py-0.5 focus:outline-none"
                    data-testid="input-remise-globale" />
                  <span className="text-muted-foreground">%</span>
                </div>
              </div>
              <div className="flex justify-between border-t border-border pt-2 font-semibold">
                <span>Total HT</span><span className="tabular-nums">{formatPrix(totalHT)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>TVA 20%</span><span className="tabular-nums">{formatPrix(tva)}</span>
              </div>
              <div className="flex justify-between border-t border-border pt-2 text-base font-bold">
                <span>Net TTC</span><span className="tabular-nums">{formatPrix(ttc)}</span>
              </div>
              {showLocation && (
                <p className="text-xs text-muted-foreground pt-1">Tarifs de location hors livraison et installation</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Product picker modal */}
      {pickerMode && (
        <ProductPickerModal
          mode={pickerMode}
          primary={primary}
          onClose={() => setPickerMode(null)}
          onSelect={item => {
            if (pickerMode === 'robot_achat' || pickerMode === 'piece') {
              addLigne(item, pickerMode === 'piece' ? 'achat_piece' : 'achat_robot');
            } else {
              addLigne(item, 'location');
            }
          }}
        />
      )}

      {/* Modale de confirmation */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setShowConfirmModal(false)}>
          <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Send className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">Confirmer l'envoi</h2>
                <p className="text-xs text-muted-foreground">Cette action est irréversible</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Votre demande sera transmise à l'équipe Jobotto. Vous recevrez une réponse dans les meilleurs délais.
            </p>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-2 rounded-lg text-sm font-medium border border-border text-foreground hover:bg-secondary/60 transition-colors">
                Annuler
              </button>
              <button onClick={handleEnvoyer} disabled={sending}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: primary }} data-testid="button-confirm-envoyer">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
